import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { MapPin, Phone, Globe, Star, ShieldCheck, Clock } from "lucide-react";

interface Props {
  provider: any;
}

export function HomeCareProviderCard({ provider }: Props) {
  const rateDisplay =
    provider.hourly_rate_min && provider.hourly_rate_max
      ? `€${provider.hourly_rate_min}–€${provider.hourly_rate_max}/hr`
      : provider.hourly_rate_min
      ? `From €${provider.hourly_rate_min}/hr`
      : null;

  return (
    <Link to={`/home-care/${provider.county_slug}/${provider.slug}`} className="block">
    <Card className="p-5 hover:shadow-lg transition-shadow duration-200 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold text-lg leading-tight truncate text-foreground">
            {provider.name}
          </h3>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{provider.county_name}</span>
            {provider.address && provider.address !== "null" && (
              <>
                <span>·</span>
                <span className="truncate">{provider.address}</span>
              </>
            )}
          </div>
        </div>
        {provider.is_hse_approved && (
          <Badge variant="secondary" className="shrink-0 gap-1 bg-accent text-accent-foreground border-border">
            <ShieldCheck className="h-3 w-3" />
            HSE
          </Badge>
        )}
      </div>

      {/* Description */}
      {provider.short_description && (
        <p className="text-sm text-muted-foreground line-clamp-2">
          {provider.short_description}
        </p>
      )}

      {/* Services */}
      {provider.services?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {provider.services.slice(0, 5).map((s: string) => (
            <Badge key={s} variant="outline" className="text-xs font-normal">
              {s}
            </Badge>
          ))}
          {provider.services.length > 5 && (
            <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
              +{provider.services.length - 5} more
            </Badge>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 pt-1 border-t text-sm">
        <div className="flex items-center gap-3 text-muted-foreground">
          {provider.rating > 0 && (
            <span className="flex items-center gap-1 font-medium text-primary">
              <Star className="h-3.5 w-3.5 fill-primary text-primary" />
              {Number(provider.rating).toFixed(1)}
            </span>
          )}
          {rateDisplay && (
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {rateDisplay}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {provider.phone && (
            <a
              href={`tel:${provider.phone}`}
              className="text-primary hover:underline flex items-center gap-1"
            >
              <Phone className="h-3.5 w-3.5" />
              Call
            </a>
          )}
          {provider.website && (
            <a
              href={provider.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline flex items-center gap-1"
            >
              <Globe className="h-3.5 w-3.5" />
              Website
            </a>
          )}
        </div>
      </div>
    </Card>
    </Link>
  );
}
